import { forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  BroadcastState,
  NoticePayload,
  ScheduledUpdatePayload,
} from "../../api/platformBroadcasts";
import { countdownTargetMs, formatCountdownMs } from "./countdown";

function ScheduledBanner({ row }: { row: ScheduledUpdatePayload }) {
  const { t } = useTranslation();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick((x) => x + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const ms = countdownTargetMs(row.scheduled_at);
  const label = formatCountdownMs(ms);
  const overdue = ms <= 0;

  return (
    <div
      className="border-b border-amber-700/30 bg-amber-500 text-amber-950 shadow-sm"
      role="status"
      aria-live="polite"
    >
      <div className="mx-auto flex max-w-[1600px] flex-col gap-0.5 px-2 py-1 sm:flex-row sm:items-center sm:gap-3 sm:px-4 sm:py-2">
        <div className="flex min-w-0 flex-1 items-baseline gap-1.5 sm:gap-2">
          <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide sm:text-xs">
            {t("platformBroadcast.scheduledLabel")}
          </span>
          <span className="truncate text-[11px] font-semibold sm:text-sm">
            {row.title}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="font-mono text-[11px] font-bold tabular-nums sm:text-sm">
            {overdue
              ? t("platformBroadcast.scheduledOverdue")
              : t("platformBroadcast.countdownPrefix", { time: label })}
          </span>
        </div>
      </div>
      {row.description ? (
        <p className="line-clamp-2 px-2 pb-1 text-[10px] opacity-90 sm:px-4 sm:pb-2 sm:text-xs">
          {row.description}
        </p>
      ) : null}
    </div>
  );
}

function NoticeStrip({ notice }: { notice: NoticePayload }) {
  const { t } = useTranslation();
  const isWarning = notice.kind === "warning";
  const bar = isWarning
    ? "bg-orange-600 text-white border-b border-orange-800/40"
    : "bg-sky-700 text-white border-b border-sky-900/40";

  return (
    <div className={bar} role="status">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-0.5 px-2 py-1 sm:flex-row sm:items-start sm:gap-2 sm:px-4 sm:py-1.5">
        <span className="shrink-0 text-[9px] font-bold uppercase opacity-90 sm:text-[10px]">
          {isWarning
            ? t("platformBroadcast.noticeWarningTag")
            : t("platformBroadcast.noticeInfoTag")}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold leading-tight sm:text-xs">
            {notice.title}
          </div>
          {notice.description ? (
            <p className="mt-0.5 line-clamp-2 text-[9px] opacity-95 sm:line-clamp-3 sm:text-[11px]">
              {notice.description}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

type Props = {
  state: BroadcastState | null;
};

export const GlobalNoticesBar = forwardRef<HTMLDivElement, Props>(
  function GlobalNoticesBar({ state }, ref) {
    const { t } = useTranslation();
    if (!state) return null;

    const su = state.scheduled_update;
    const showScheduled = su && su.phase === "announced";

    const notices = state.notices || [];

    if (!showScheduled && notices.length === 0) return null;

    return (
      <div
        ref={ref}
        className="fixed left-0 right-0 top-0 z-[120] flex max-h-[40vh] flex-col overflow-y-auto shadow-md sm:max-h-[30vh]"
      >
        {showScheduled && su ? <ScheduledBanner row={su} /> : null}
        {notices.map((n) => (
          <NoticeStrip key={n.id} notice={n} />
        ))}
        <span className="sr-only">{t("platformBroadcast.activeNoticesRegion")}</span>
      </div>
    );
  }
);
